import * as Sentry from '@sentry/vue';
import { safe } from './safe';
import { state } from './state';

type SpanAttributes = Record<string, unknown>;
type SpanAttrCast = Record<string, string | number | boolean | undefined>;

// Returns an INACTIVE span — caller must call span.end() manually. Use withSpan
// for auto-managed spans.
export function startSpan(
  name: string,
  op: string,
  attributes: SpanAttributes = {}
): ReturnType<typeof Sentry.startInactiveSpan> | null {
  return (
    safe('startSpan', () =>
      Sentry.startInactiveSpan({
        name,
        op,
        attributes: {
          ...attributes,
          'kiosk.session_id': state.sessionContext.session_id,
        } as SpanAttrCast,
      })
    ) ?? null
  );
}

// CRITICAL: this function MUST NEVER fail due to Sentry errors. The wrapped
// function must always execute exactly once. Payment flows depend on this
// guarantee. Three failure scenarios are handled explicitly:
//   1. fn() succeeded but Sentry threw afterward (e.g. span cleanup) → return fn's result
//   2. fn() executed and threw → rethrow the original business-logic error
//   3. Sentry.startSpan failed before fn() could execute → execute fn() without tracing
export async function withSpan<T>(
  name: string,
  op: string,
  fn: () => Promise<T>,
  attributes: SpanAttributes = {}
): Promise<T> {
  if (!state.initialized) return fn();

  let fnExecuted = false;
  let fnSucceeded = false;
  let fnResult: T | undefined;
  let fnError: unknown = null;

  try {
    return await Sentry.startSpan(
      {
        name,
        op,
        attributes: {
          ...attributes,
          'kiosk.session_id': state.sessionContext.session_id,
        } as Record<string, string | number | boolean | undefined>,
      },
      async (span) => {
        fnExecuted = true;
        try {
          fnResult = await fn();
          fnSucceeded = true;
          try {
            span.setStatus({ code: 1 }); // OK
          } catch {
            // Telemetry failure must not affect result
          }
          return fnResult;
        } catch (error) {
          fnError = error;
          try {
            span.setStatus({ code: 2, message: (error as Error).message });
          } catch {
            // Ignore span status error
          }
          throw error;
        }
      }
    );
  } catch (error) {
    if (fnSucceeded) return fnResult as T;
    if (fnExecuted && !fnSucceeded) throw fnError;

    console.warn(
      '[Sentry] withSpan failed, executing without span:',
      (error as Error).message
    );
    return fn();
  }
}

type MetricUnit =
  | 'none'
  | 'nanosecond'
  | 'microsecond'
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'byte'
  | 'kilobyte'
  | 'megabyte'
  | 'gigabyte'
  | 'percent'
  | 'ratio';

// Sentry's public types for the metrics API intentionally omit the `tags`
// field (per-version drift). Using one local shim means (a) one cast, (b) the
// v8→v9 rename `metrics.increment` → `metrics.count` lives in a single place
// and covers every counter/gauge/distribution/set call.
interface MetricsShim {
  gauge?: (n: string, v: number, o: unknown) => void;
  increment?: (n: string, v: number, o: unknown) => void;
  count?: (n: string, v: number, o: unknown) => void;
  distribution?: (n: string, v: number, o: unknown) => void;
  set?: (n: string, v: string | number, o: unknown) => void;
}

function metricsApi(): MetricsShim {
  return Sentry.metrics as unknown as MetricsShim;
}

// Every emitter goes through here. Metrics bill by ingested bytes and this
// fleet was on track for 6.6x the monthly quota in one month, so a session
// either reports its metrics or it does not — see METRICS_SESSION_SAMPLE_RATE
// for why the decision is per session rather than per call.
//
// Errors, logs and spans are unaffected: they have their own quotas and their
// own sampling, and a dropped metric must never hide a failure.
function metricsSampledIn(): boolean {
  return state.metricsSampledIn;
}

// Default tag envelope shared by every metric call — kiosk_slug and
// environment are the slice dimensions we always need in Sentry dashboards.
function metricTags(extra: Record<string, unknown>): Record<string, unknown> {
  return {
    ...extra,
    kiosk_slug: state.kioskContext.slug ?? 'unknown',
    environment: state.environment,
  };
}

export function trackMetric(
  name: string,
  value: number,
  unit: MetricUnit = 'none',
  tags: Record<string, unknown> = {}
): void {
  if (!metricsSampledIn()) return;
  safe('trackMetric', () => {
    metricsApi().gauge?.(name, value, { unit, tags: metricTags(tags) });
  });
}

export function incrementCounter(
  name: string,
  value = 1,
  tags: Record<string, unknown> = {}
): void {
  if (!metricsSampledIn()) return;
  safe('incrementCounter', () => {
    const api = metricsApi();
    // Sentry v9 renamed metrics.increment → metrics.count.
    const increment = api.increment ?? api.count;
    increment?.(name, value, { tags: metricTags(tags) });
  });
}

export function trackDistribution(
  name: string,
  value: number,
  unit: MetricUnit = 'none',
  tags: Record<string, unknown> = {}
): void {
  if (!metricsSampledIn()) return;
  safe('trackDistribution', () => {
    metricsApi().distribution?.(name, value, {
      unit,
      tags: metricTags(tags),
    });
  });
}

export function trackSet(
  name: string,
  value: string | number,
  tags: Record<string, unknown> = {}
): void {
  if (!metricsSampledIn()) return;
  safe('trackSet', () => {
    metricsApi().set?.(name, value, { tags: metricTags(tags) });
  });
}

interface ChromePerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

// Chrome-only performance.memory API — no-op on browsers that don't support it.
// Call at key points (session start, after photo upload, after generation).
export function trackMemoryUsage(context = 'general'): void {
  if (!state.initialized) return;

  const memory = (
    performance as Performance & { memory?: ChromePerformanceMemory }
  ).memory;
  if (typeof memory === 'undefined') return;

  const heapUsedMB = memory.usedJSHeapSize / (1024 * 1024);
  const heapTotalMB = memory.totalJSHeapSize / (1024 * 1024);
  const heapLimitMB = memory.jsHeapSizeLimit / (1024 * 1024);

  trackMetric('memory.heap_used_mb', heapUsedMB, 'megabyte', { context });
  trackMetric('memory.heap_total_mb', heapTotalMB, 'megabyte', { context });
  trackMetric('memory.heap_limit_mb', heapLimitMB, 'megabyte', { context });
  trackMetric(
    'memory.heap_usage_percent',
    (heapUsedMB / heapLimitMB) * 100,
    'percent',
    { context }
  );
}

// Build `sentry-trace` + `baggage` headers for distributed tracing on outbound
// fetch calls. Length caps match Sentry envelope limits (defensive — a very
// long baggage header can bounce at CDN/proxy level).
export function getTraceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  safe('getTraceHeaders', () => {
    const span = Sentry.getActiveSpan?.();
    if (!span) return;
    const traceHeader = Sentry.spanToTraceHeader?.(span);
    const baggageHeader = Sentry.spanToBaggageHeader?.(span);
    if (typeof traceHeader === 'string' && traceHeader.length < 500) {
      headers['sentry-trace'] = traceHeader;
    }
    if (typeof baggageHeader === 'string' && baggageHeader.length < 2000) {
      headers.baggage = baggageHeader;
    }
  });
  return headers;
}
